import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, withRouter } from 'react-router-dom';

import GGApi from '../../../utils/GGApi.js';
import GG_loader from '../../general_components/GG_loader/GG_loader.js';
import MasterButton from '../../general_components/MasterButton/MasterButton.js';
import MUI_Slider from '../../general_components/material_ui/mui_Slider.js';
import Gg_plus_feature_cards from '../../GG_plus/Gg_plus_feature_cards.js';
import Gg_plus_reactivate_link from '../../GG_plus/Gg_plus_reactivate_link.js';
import confetti_three from '../../../images/illustrations/confetti_three.png';
import confetti_two from '../../../images/illustrations/confetti_two.png';
import gg_plus_logo from '../../../images/logos/gg_plus/gg_plus_206.png';
import {
  approximateTrialEndDate,
  formatMembershipDate,
  isGgPlusActive,
  isMembershipCancelScheduled,
  membershipCancelNotice,
  membershipPeriodEndLabel,
  membershipPriceLabel,
  membershipStatusLabel,
  readEntitlements,
  readReturnTo,
  readGgPlusAttribution,
  parseGgPlusPrice,
  resolveMembershipBackTarget,
  storeMembershipBackTarget,
  backTargetFromSource,
  MEMBERSHIP_DEFAULT_BACK,
} from '../../../utils/membershipUrl.js';
import {
  logGgPlusCancelClicked,
  logGgPlusCheckoutStarted,
  logGgPlusManageClicked,
  logGgPlusMembershipPageViewed,
  logGgPlusPaymentIssueViewed,
  logGgPlusPriceSelected,
  logGgPlusSubscriptionStarted,
  logGgPlusTrialStarted,
} from '../../../utils/ggPlusAnalytics.js';
import '../../GG_plus/Gg_plus_shared.css';

/** Slider anchor labels for GG+ pay-what-you-want pricing. */
const GG_PLUS_SLIDER_MARKS = [
  {
    value: 5,
    label: (
      <div className="slider-mark-container">
        <div className="slider-mark-label">
          <strong>$5</strong>
          <div>Cover GG+<br />costs</div>
        </div>
      </div>
    ),
  },
  {
    value: 7,
    label: (
      <div className="slider-mark-container">
        <img src={confetti_two} alt="" className="slider-mark-confetti" />
        <div className="slider-mark-label">
          <strong>$7</strong>
          <div>Keep GG+<br />running</div>
        </div>
      </div>
    ),
  },
  {
    value: 11,
    label: (
      <div className="slider-mark-container">
        <img src={confetti_three} alt="" className="slider-mark-confetti" />
        <div className="slider-mark-label">
          <strong>$11</strong>
          <div>Build new<br />GG+ tools</div>
        </div>
      </div>
    ),
  },
];

/**
 * GG+ membership hub — trial start, reactivate, active manage, and post-checkout refresh.
 */
class Membership extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      checkoutLoading: false,
      price: 7,
      entitlements: null,
      trialEligible: true,
      source: null,
      returnTo: null,
    };
    this.startCheckout = this.startCheckout.bind(this);
    this.startPortal = this.startPortal.bind(this);
    this.refreshEntitlements = this.refreshEntitlements.bind(this);
    this.handlePriceChange = this.handlePriceChange.bind(this);
    this.lastLoggedPrice = 7;
    this.checkoutSuccessLogged = false;
    this.paymentIssueLogged = false;
    this.pageViewLogged = false;
  }

  handlePriceChange(input) {
    const price = Number(input);
    this.setState({ price });
    if (price !== this.lastLoggedPrice) {
      this.lastLoggedPrice = price;
      logGgPlusPriceSelected({
        price,
        ...readGgPlusAttribution(this.props.location),
      });
    }
  }

  componentDidMount() {
    const params = new URLSearchParams(this.props.location.search);
    const fromStripeCheckout = params.get('checkout') === 'success';
    const priceFromUrl = parseGgPlusPrice(params.get('price'));

    this.setState({
      source: params.get('source'),
      returnTo: readReturnTo(this.props.location),
      ...(priceFromUrl != null ? { price: priceFromUrl } : {}),
    });
    if (priceFromUrl != null) {
      this.lastLoggedPrice = priceFromUrl;
    }

    // Remember in-app back target before Stripe redirects pollute browser history.
    if (!fromStripeCheckout) {
      const source = params.get('source');
      storeMembershipBackTarget(
        readReturnTo(this.props.location)
          || backTargetFromSource(source)
          || MEMBERSHIP_DEFAULT_BACK
      );
    }

    this.refreshEntitlements();
  }

  // Refresh entitlements from server; handle post-checkout return_to navigation.
  refreshEntitlements() {
    this.setState({ loading: true });
    Promise.all([
      GGApi.checkToken(),
      GGApi.trial_eligible().catch(() => ({ eligible: true })),
    ]).then(([, trialRes]) => {
      const ent = readEntitlements();
      const trialEligible = !!(trialRes && trialRes.eligible);
      const level = ent && ent.level;
      const showManageView = isGgPlusActive(ent) || level === 'past_due';
      const attribution = readGgPlusAttribution(this.props.location);

      this.setState({
        loading: false,
        entitlements: ent,
        trialEligible,
      });

      // Analytics: page view + payment issue (once per mount).
      if (!this.pageViewLogged) {
        this.pageViewLogged = true;
        logGgPlusMembershipPageViewed({
          ...attribution,
          view: showManageView ? 'manage' : 'checkout',
          level,
        });
      }
      if (level === 'past_due' && !this.paymentIssueLogged) {
        this.paymentIssueLogged = true;
        logGgPlusPaymentIssueViewed({ source: 'membership_page' });
      }

      const params = new URLSearchParams(this.props.location.search);
      if (params.get('checkout') === 'success') {
        // Strip checkout flag so refresh/back don't re-trigger success handling.
        const nextParams = new URLSearchParams();
        if (params.get('source')) nextParams.set('source', params.get('source'));
        if (params.get('return_to')) nextParams.set('return_to', params.get('return_to'));
        const qs = nextParams.toString();
        this.props.history.replace(
          `${this.props.location.pathname}${qs ? `?${qs}` : ''}`
        );

        // Analytics: trial vs paid subscription start (once per successful return).
        if (!this.checkoutSuccessLogged && isGgPlusActive(ent)) {
          this.checkoutSuccessLogged = true;
          const subPrice = ent.subscription && ent.subscription.price;
          const price = subPrice != null ? subPrice : this.state.price;
          if (level === 'trial') {
            logGgPlusTrialStarted({ price, ...attribution });
          } else {
            logGgPlusSubscriptionStarted({ price, ...attribution });
          }
        }

        const returnTo = readReturnTo(this.props.location);
        if (returnTo && isGgPlusActive(ent)) {
          this.props.history.replace(returnTo);
        }
      }
    });
  }

  startCheckout() {
    if (this.state.checkoutLoading) return;
    logGgPlusCheckoutStarted({
      price: this.state.price,
      trial_eligible: this.state.trialEligible,
      ...readGgPlusAttribution(this.props.location),
    });
    storeMembershipBackTarget(resolveMembershipBackTarget(this.props.location));
    this.setState({ checkoutLoading: true });
    GGApi.create_checkout_session(this.state.price, {
      source: this.state.source,
      return_to: this.state.returnTo,
    }).then((response) => {
      if (response && response.url) {
        window.location.href = response.url;
      } else {
        this.setState({ checkoutLoading: false });
        alert('Something went wrong. Please try again.');
      }
    });
  }

  // Open a GG+ portal deep link (payment update or cancel — never generic portal home).
  startPortal(flow) {
    if (this.state.checkoutLoading) return;
    if (flow === 'subscription_cancel') {
      logGgPlusCancelClicked({ source: this.state.source || 'membership_page' });
    } else {
      logGgPlusManageClicked({ action: flow, source: this.state.source || 'membership_page' });
    }
    storeMembershipBackTarget(resolveMembershipBackTarget(this.props.location));
    this.setState({ checkoutLoading: true });
    GGApi.create_portal_session(flow).then((response) => {
      if (response && response.url) {
        window.location.href = response.url;
      } else {
        this.setState({ checkoutLoading: false });
        alert(response && response.message ? response.message : 'Something went wrong. Please try again.');
      }
    });
  }

  renderCheckoutSummary(trialMode) {
    const price = this.state.price;
    const trialEnd = approximateTrialEndDate(30);
    return (
      <div className="membership_summary_rows">
        <div className="membership_summary_row">
          <span>Today</span>
          <span>$0</span>
        </div>
        {trialMode ? (
          <>
            <div className="membership_summary_row">
              <span>After trial</span>
              <span>${price}/month</span>
            </div>
            <div className="membership_summary_row">
              <span>Trial ends</span>
              <span>{trialEnd}</span>
            </div>
          </>
        ) : (
          <div className="membership_summary_row">
            <span>Monthly price</span>
            <span>${price}/month</span>
          </div>
        )}
      </div>
    );
  }

  renderCheckoutFlow() {
    const trialMode = this.state.trialEligible;
    const ctaText = trialMode ? 'Continue to trial' : 'Continue to payment';

    return (
      <div className="membership_main_card">
        <h2 className="membership_upgrade_title">Upgrade Account</h2>
        <img src={gg_plus_logo} alt="GG+" className="gg_plus_logo gg_plus_logo_large" />
        <p className="membership_slider_headline">Choose your monthly GG+ price</p>
        <p className="membership_slider_body">
          Every price unlocks the same GG+ features. Choose the amount that feels right for your
          family, from covering your GG+ costs to helping us build more tools for you to use.
        </p>

        <MUI_Slider
          onChange={this.handlePriceChange}
          value={Number(this.state.price)}
          min={3}
          max={12}
          marks={GG_PLUS_SLIDER_MARKS}
        />

        {!trialMode ? (
          <p className="membership_status_message">
            Restart GG+ to use monthly gifts, gift matching, and other GG+ tools again.
          </p>
        ) : null}

        {trialMode ? (
          <p className="membership_trial_headline">Start your 1-month free trial</p>
        ) : null}

        {this.renderCheckoutSummary(trialMode)}

        <MasterButton
          buttonText={ctaText}
          handleButtonClick={this.startCheckout}
          notSticky={true}
          inactive_button={this.state.checkoutLoading}
        />

        {trialMode ? (
          <p className="membership_fine_print">
            You&apos;ll add a payment method next. You will not be charged during your trial. After your trial ends, your membership
            continues at your chosen price unless you cancel before then.
          </p>
        ) : null}

        <p className="membership_fine_print">
          Cancel anytime. By continuing you agree to our{' '}
          <Link to="/legal?active=terms" className="green_text">
            Terms of Use
          </Link>
          .
        </p>
      </div>
    );
  }

  renderActiveMembership() {
    const ent = this.state.entitlements;
    const level = ent && ent.level;
    const sub = ent && ent.subscription;
    const priceLabel = membershipPriceLabel(ent) || `$${this.state.price}/month`;
    const periodEnd = sub && formatMembershipDate(sub.currentPeriodEnd);
    const activeSince = sub && formatMembershipDate(sub.currentPeriodStart);
    const cancelScheduled = isMembershipCancelScheduled(ent);
    const isFullyCanceled = level === 'canceled';
    const cancelNotice = membershipCancelNotice(ent);

    let statusMessage = null;
    if (level === 'past_due') {
      statusMessage = (
        <p className="membership_status_message membership_past_due">
          We couldn&apos;t process your GG+ payment. Monthly gifts and gift matching are
          paused until your payment method is updated.
        </p>
      );
    } else if (cancelNotice) {
      statusMessage = <p className="membership_status_message">{cancelNotice}</p>;
    }

    const showManageActions = !cancelScheduled && !isFullyCanceled;

    return (
      <div className="membership_main_card">
        <img src={gg_plus_logo} alt="GG+" className="gg_plus_logo gg_plus_logo_large" />
        <h2 className="membership_details_heading">Membership Details</h2>

        {statusMessage}

        <div className="membership_summary_rows">
          <div className="membership_summary_row">
            <span>Status</span>
            <span>{membershipStatusLabel(ent)}</span>
          </div>
          {showManageActions ? (
            <div className="membership_summary_row">
              <span>Price</span>
              <span>{priceLabel}</span>
            </div>
          ) : null}
          {activeSince && showManageActions ? (
            <div className="membership_summary_row">
              <span>Active since</span>
              <span>{activeSince}</span>
            </div>
          ) : null}
          {periodEnd ? (
            <div className="membership_summary_row">
              <span>{membershipPeriodEndLabel(ent)}</span>
              <span>{periodEnd}</span>
            </div>
          ) : null}
        </div>

        <hr className="membership_details_divider" />

        {cancelScheduled ? (
          <Gg_plus_reactivate_link
            label="Keep GG+"
            onSuccess={() => this.refreshEntitlements()}
          />
        ) : null}

        {showManageActions ? (
          <>
            <MasterButton
              buttonText="Update payment method"
              handleButtonClick={() => this.startPortal('payment_method_update')}
              notSticky={true}
              inactive_button={this.state.checkoutLoading}
            />

            <p
              className={`membership_cancel_link secondary_dark bold${this.state.checkoutLoading ? ' membership_cancel_link_disabled' : ''}`}
              onClick={this.state.checkoutLoading ? undefined : () => this.startPortal('subscription_cancel')}
            >
              Cancel GG+
            </p>
          </>
        ) : isFullyCanceled ? (
          // Canceled with access until period end — checkout appears on this page once access lapses.
          <p className="membership_status_message">
            {periodEnd
              ? `After ${periodEnd}, GG+ features will pause. You can subscribe again if you want from this page.`
              : 'GG+ features are paused. You can subscribe again from this page.'}
          </p>
        ) : null}
      </div>
    );
  }

  render() {
    if (this.state.loading || this.state.checkoutLoading) {
      return <GG_loader />;
    }

    const ent = this.state.entitlements;
    const isGgPlus = isGgPlusActive(ent);
    const level = ent && ent.level;
    const showManageView = isGgPlus || level === 'past_due';

    return (
      <div className="membership_page">
      <main className="membership_page_main membership_page_wrapper">
        <Helmet>
          <title>GG+ Membership | Greatest Gift</title>
          <meta
            name="description"
            content="Manage your GG+ membership on Greatest Gift."
          />
        </Helmet>

        {showManageView ? this.renderActiveMembership() : this.renderCheckoutFlow()}

        <Gg_plus_feature_cards
          sectionTitle={showManageView ? 'Your GG+ Features' : 'Unlock Tools With GG+'}
        />

        <div className="buffer" />
      </main>
      </div>
    );
  }
}

export default withRouter(Membership);
